import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from './styles/GlobalStyle';
import { theme } from './styles/theme';
import { SelectedOption } from './types';
import LandingPage from './pages/LandingPage';
import ConfiguratorPage from './pages/ConfiguratorPage';
import FinalPage from './pages/FinalPage';
import Footer from './components/Footer';
import { usePageTracker } from './store/usePageTracker';
import { DEFAULT_LANG, Lang } from './config/locale';

export type { Lang };

function App() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [finalSelections, setFinalSelections] = useState<SelectedOption[]>([]);
  const [finalPrice, setFinalPrice] = useState(0);
  const [lang, setLang] = useState<Lang>(DEFAULT_LANG);

  // 문서 제목/설명은 정적 index.html(영문 기본값)을 현재 언어에 맞춰 교체한다.
  useEffect(() => {
    const isKo = lang === 'ko';
    document.documentElement.lang = lang;
    document.title = isKo
      ? '우수건설기계 - 분무기 맞춤 구성'
      : 'WOOSU Construction Machinery - Sprayer Configurator';
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute(
      'content',
      isKo
        ? '우수건설기계 - 고압 세척기 분무기 맞춤 구성 시스템'
        : 'WOOSU Construction Machinery - High-Pressure Sprayer Configurator',
    );
  }, [lang]);

  // 페이지 결정: landing(기본) → config(옵션선택) → final(최종)
  const pageParam = searchParams.get('page');
  let page: 'landing' | 'config' | 'final' = 'landing';
  if (pageParam === 'final' && finalSelections.length > 0) {
    page = 'final';
  } else if (pageParam === 'config' || pageParam === 'final' || searchParams.has('step')) {
    // page=final 인데 메모리 선택값이 없으면(새로고침) configurator가 URL에서 복원 후
    // onComplete로 최종 페이지로 보낸다.
    page = 'config';
  }

  // 페이지 방문 추적
  usePageTracker(
    page === 'final' ? '/final' : page === 'config' ? '/configurator' : '/',
    page === 'final' ? '최종 제품 페이지' : page === 'config' ? '제품 구성 선택' : '메인 페이지',
  );

  const handleStartCustomize = useCallback(() => {
    setSearchParams({ page: 'config' });
  }, [setSearchParams]);

  const handleComplete = useCallback((selections: SelectedOption[], totalPrice: number) => {
    setFinalSelections(selections);
    setFinalPrice(totalPrice);
    setSearchParams((prev) => {
      prev.delete('step');
      prev.set('page', 'final');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const handleReset = useCallback(() => {
    setFinalSelections([]);
    setFinalPrice(0);
    setSearchParams({ page: 'config' }, { replace: true });
  }, [setSearchParams]);

  const handleLogoClick = useCallback(() => {
    setFinalSelections([]);
    setFinalPrice(0);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleGoToStep = useCallback((stepIndex: number) => {
    setFinalSelections([]);
    setFinalPrice(0);
    setSearchParams((prev) => {
      prev.delete('page');
      prev.set('step', String(stepIndex));
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {page === 'landing' && (
        <LandingPage
          onStartCustomize={handleStartCustomize}
          lang={lang}
          onLangChange={setLang}
        />
      )}
      {page === 'config' && (
        <ConfiguratorPage onComplete={handleComplete} onLogoClick={handleLogoClick} lang={lang} onLangChange={setLang} />
      )}
      {page === 'final' && (
        <FinalPage
          selections={finalSelections}
          totalPrice={finalPrice}
          onReset={handleReset}
          onLogoClick={handleLogoClick}
          onGoToStep={handleGoToStep}
          lang={lang}
          onLangChange={setLang}
        />
      )}
      {page !== 'landing' && <Footer lang={lang} />}
    </ThemeProvider>
  );
}

export default App;
